import { infiniteQueryOptions, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { mapResponseError } from '@mybusinessmate/api-client';
import type {
  AddPersonInput,
  ClientReview,
  ClientReviewInput,
  CreateTaskInput,
  FixVersionInput,
  ListReviewsParams,
  MilestoneInput,
  RejectSubmissionInput,
  ResolveFixInput,
  ResolvePersonRequestInput,
  ReviewQueue,
  SetProgressInput,
  SubmitVersionInput,
  TaskDetail,
  TaskListResponse,
  TaskSettingsInput,
  TaskTab,
  TaskType,
  UpdateTaskInput,
  Version,
  VersionList,
  WaitingTasksResponse,
  Worker,
} from '@mybusinessmate/schema';
import { api } from './api';
import { projectsKey } from './projects-api';

export interface TaskFilters {
  projectId: string;
  tab: TaskTab;
  q?: string;
  type?: TaskType;
  /** A staff id, or 'none' for tasks nobody is on. */
  assigneeId?: string;
}

const PAGE_SIZE = 30;

/** Every task query hangs off this key, so one invalidate refreshes lists and details after a change. */
export const tasksKey = ['tasks'] as const;

async function fetchPage(filters: TaskFilters, cursor: string | undefined): Promise<TaskListResponse> {
  const res = await api.api.tasks.$get({
    query: {
      projectId: filters.projectId,
      tab: filters.tab,
      limit: String(PAGE_SIZE),
      ...(filters.q ? { q: filters.q } : {}),
      ...(filters.type ? { type: filters.type } : {}),
      ...(filters.assigneeId ? { assigneeId: filters.assigneeId } : {}),
      ...(cursor ? { cursor } : {}),
    },
  });
  if (!res.ok) throw await mapResponseError(res);
  return res.json();
}

export const taskListQuery = (filters: TaskFilters) =>
  infiniteQueryOptions({
    queryKey: [...tasksKey, 'list', filters] as const,
    queryFn: ({ pageParam }) => fetchPage(filters, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

/** The review queue hangs off its own key: a version moving through QA changes it. */
export const reviewsKey = ['reviews'] as const;

export const taskQuery = (id: string) =>
  queryOptions({
    queryKey: [...tasksKey, 'detail', id] as const,
    queryFn: async (): Promise<TaskDetail> => {
      const res = await api.api.tasks[':id'].$get({ param: { id } });
      if (!res.ok) throw await mapResponseError(res);
      return res.json();
    },
  });

/**
 * A task changing moves the project's stored progress rollup (and its task counts), so project queries are
 * refreshed together with task queries.
 */
function useRefreshAfterTaskWrite() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: tasksKey }),
      queryClient.invalidateQueries({ queryKey: projectsKey }),
      queryClient.invalidateQueries({ queryKey: reviewsKey }),
    ]);
}

export function useCreateTask() {
  const refresh = useRefreshAfterTaskWrite();
  return useMutation({
    mutationFn: async (json: CreateTaskInput): Promise<TaskDetail> => {
      const res = await api.api.tasks.$post({ json });
      if (!res.ok) throw await mapResponseError(res);
      return res.json();
    },
    onSuccess: refresh,
  });
}

export function useUpdateTask(id: string) {
  const refresh = useRefreshAfterTaskWrite();
  return useMutation({
    mutationFn: async (json: UpdateTaskInput): Promise<TaskDetail> => {
      const res = await api.api.tasks[':id'].$put({ param: { id }, json });
      if (!res.ok) throw await mapResponseError(res);
      return res.json();
    },
    onSuccess: refresh,
  });
}

/**
 * The workspace writes (Figma A05.4) all answer with the whole task, so the page updates from the response without a
 * second round trip, and the lists and the project's rollup refresh behind it.
 */
function useWorkspaceMutation<V>(id: string, call: (variables: V) => Promise<Response>) {
  const queryClient = useQueryClient();
  const refresh = useRefreshAfterTaskWrite();
  return useMutation({
    mutationFn: async (variables: V): Promise<TaskDetail> => {
      const res = await call(variables);
      if (!res.ok) throw await mapResponseError(res);
      // Every workspace route declares the task as its 200 body, so a success is always that.
      return (await res.json()) as TaskDetail;
    },
    onSuccess: (task) => {
      queryClient.setQueryData(taskQuery(id).queryKey, task);
      return refresh();
    },
  });
}

export const useSetProgress = (id: string) =>
  useWorkspaceMutation(id, (json: SetProgressInput) => api.api.tasks[':id'].progress.$put({ param: { id }, json }));

export const useSetMilestones = (id: string) =>
  useWorkspaceMutation(id, (milestones: MilestoneInput[]) =>
    api.api.tasks[':id'].milestones.$put({ param: { id }, json: { milestones } }),
  );

export const useUpdateTaskSettings = (id: string) =>
  useWorkspaceMutation(id, (json: TaskSettingsInput) => api.api.tasks[':id'].settings.$patch({ param: { id }, json }));

export const useAddPerson = (id: string) =>
  useWorkspaceMutation(id, (json: AddPersonInput) => api.api.tasks[':id'].people.$post({ param: { id }, json }));

export const useRemovePerson = (id: string) =>
  useWorkspaceMutation(id, (staffId: string) =>
    api.api.tasks[':id'].people[':staffId'].$delete({ param: { id, staffId } }),
  );

export const useResolvePersonRequest = (id: string) =>
  useWorkspaceMutation(id, (v: { requestId: string } & ResolvePersonRequestInput) =>
    api.api.tasks[':id'].people.requests[':requestId'].$post({
      param: { id, requestId: v.requestId },
      json: { approve: v.approve, confirmOverCapacity: v.confirmOverCapacity },
    }),
  );

/** The "Add people" picker: active leads and specialists, with how loaded each is. */
export const workersQuery = (q: string) =>
  queryOptions({
    queryKey: [...tasksKey, 'workers', q] as const,
    queryFn: async (): Promise<Worker[]> => {
      const res = await api.api.tasks.people.available.$get({ query: q ? { q } : {} });
      if (!res.ok) throw await mapResponseError(res);
      return (await res.json()).items;
    },
    staleTime: 30_000,
  });

/** A client's "Waiting for you": what the team has asked them to review, across all their projects. */
export const waitingQuery = () =>
  queryOptions({
    queryKey: [...tasksKey, 'waiting'] as const,
    queryFn: async (): Promise<WaitingTasksResponse> => {
      const res = await api.api.tasks['waiting-for-you'].$get();
      if (!res.ok) throw await mapResponseError(res);
      return res.json();
    },
  });

// --- Versions and the review gate (Figma A05.4 versions, A05.5, A05.6) ---------------------------------------------

/** Versions v1 and up of a task, newest first. Version 0 is the brief on the task itself. */
export const versionsQuery = (taskId: string) =>
  queryOptions({
    queryKey: [...tasksKey, 'versions', taskId] as const,
    queryFn: async (): Promise<Version[]> => {
      const res = await api.api.tasks[':id'].submissions.$get({ param: { id: taskId } });
      if (!res.ok) throw await mapResponseError(res);
      return ((await res.json()) as VersionList).items;
    },
  });

/**
 * Starts a version, or (with `subId`) rewrites a draft. A version moving changes the task (status, progress), its
 * project and the queue, so all of those refresh behind it.
 */
export function useSaveVersion(taskId: string) {
  const refresh = useRefreshAfterTaskWrite();
  return useMutation({
    mutationFn: async ({ input, subId }: { input: SubmitVersionInput; subId?: string }): Promise<Version> => {
      const res = subId
        ? await api.api.tasks[':id'].submissions[':subId'].$put({ param: { id: taskId, subId }, json: input })
        : await api.api.tasks[':id'].submissions.$post({ param: { id: taskId }, json: input });
      if (!res.ok) throw await mapResponseError(res);
      return (await res.json()) as Version;
    },
    onSuccess: refresh,
  });
}

export function useRejectVersion(taskId: string) {
  const refresh = useRefreshAfterTaskWrite();
  return useMutation({
    mutationFn: async ({ subId, ...json }: { subId: string } & RejectSubmissionInput): Promise<Version> => {
      const res = await api.api.tasks[':id'].submissions[':subId'].reject.$post({ param: { id: taskId, subId }, json });
      if (!res.ok) throw await mapResponseError(res);
      return (await res.json()) as Version;
    },
    onSuccess: refresh,
  });
}

export function usePublishVersion(taskId: string) {
  const refresh = useRefreshAfterTaskWrite();
  return useMutation({
    mutationFn: async ({
      subId,
      requiresClientApproval,
    }: {
      subId: string;
      requiresClientApproval?: boolean;
    }): Promise<Version> => {
      const res = await api.api.tasks[':id'].submissions[':subId'].publish.$post({
        param: { id: taskId, subId },
        json: requiresClientApproval === undefined ? {} : { requiresClientApproval },
      });
      if (!res.ok) throw await mapResponseError(res);
      return (await res.json()) as Version;
    },
    onSuccess: refresh,
  });
}

/** A small fix to a version already sent: a lead edits it in place, a specialist's is only proposed. */
export function useFixVersion(taskId: string) {
  const refresh = useRefreshAfterTaskWrite();
  return useMutation({
    mutationFn: async ({ subId, input }: { subId: string; input: FixVersionInput }): Promise<Version> => {
      const res = await api.api.tasks[':id'].submissions[':subId'].fix.$post({
        param: { id: taskId, subId },
        json: input,
      });
      if (!res.ok) throw await mapResponseError(res);
      return (await res.json()) as Version;
    },
    onSuccess: refresh,
  });
}

/** A lead applies, or declines, a specialist's proposed fix. */
export function useResolveFix(taskId: string) {
  const refresh = useRefreshAfterTaskWrite();
  return useMutation({
    mutationFn: async ({ subId, ...json }: { subId: string } & ResolveFixInput): Promise<Version> => {
      const res = await api.api.tasks[':id'].submissions[':subId'].fix.resolve.$post({
        param: { id: taskId, subId },
        json,
      });
      if (!res.ok) throw await mapResponseError(res);
      return (await res.json()) as Version;
    },
    onSuccess: refresh,
  });
}

export type ReviewFilters = Pick<ListReviewsParams, 'tab' | 'sort'> &
  Partial<Pick<ListReviewsParams, 'q' | 'type' | 'pod'>>;

/** The lead's queue: versions waiting on QA, or sent back. */
export const reviewsQuery = (filters: ReviewFilters) =>
  queryOptions({
    queryKey: [...reviewsKey, filters] as const,
    queryFn: async (): Promise<ReviewQueue> => {
      const res = await api.api.reviews.$get({
        query: {
          tab: filters.tab,
          sort: filters.sort,
          ...(filters.q ? { q: filters.q } : {}),
          ...(filters.type ? { type: filters.type } : {}),
          ...(filters.pod ? { pod: filters.pod } : {}),
        },
      });
      if (!res.ok) throw await mapResponseError(res);
      return res.json();
    },
  });

// --- The client's review (Figma C05.2) ---------------------------------------------------------------------------

/** What the client sees of one task: only the versions published to them. */
export const clientReviewQuery = (taskId: string) =>
  queryOptions({
    queryKey: [...tasksKey, 'client-review', taskId] as const,
    queryFn: async (): Promise<ClientReview> => {
      const res = await api.api.tasks[':id'].review.$get({ param: { id: taskId } });
      if (!res.ok) throw await mapResponseError(res);
      return res.json();
    },
  });

/** Approve, or ask for changes. The answer is the review as it stands now, so the page updates without a second trip. */
export function useClientRespond(taskId: string) {
  const queryClient = useQueryClient();
  const refresh = useRefreshAfterTaskWrite();
  return useMutation({
    mutationFn: async ({ subId, input }: { subId: string; input: ClientReviewInput }): Promise<ClientReview> => {
      const res = await api.api.tasks[':id'].submissions[':subId']['client-review'].$post({
        param: { id: taskId, subId },
        json: input,
      });
      if (!res.ok) throw await mapResponseError(res);
      return (await res.json()) as ClientReview;
    },
    onSuccess: (review) => {
      queryClient.setQueryData(clientReviewQuery(taskId).queryKey, review);
      return refresh();
    },
  });
}
